import React, { useEffect, useState } from 'react';

// components and helpers
import { hdeCASEMapping } from './helpers/HdeFieldMapping';

// mui
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CircularProgress } from '@mui/material';
import utils from '../../../../utils/utils';
import { useSelector } from 'react-redux';

interface DataMappingProps {
    headers: string[];
    mappedFields: any;
    setMappedFields: (mappedItems) => void;
    fileType: string;
}

const DataMapping: React.FC<DataMappingProps> = ({
    headers,
    mappedFields,
    setMappedFields,
    fileType,
}) => {
    const [usedFields, setUsedFields] = useState([]);
    const [fieldMapping, setFieldMapping] = useState(null);
    const savedMapping = useSelector(
        (state: any) => state.workspace.savedMapping
    );

    useEffect(() => {
        const selectedFields = Object.keys(
            Object.entries(mappedFields).reduce(
                (a, [k, v]) => (v === null ? a : ((a[k] = v), a)),
                {}
            )
        );
        setUsedFields(selectedFields);
    }, [mappedFields]);

    useEffect(() => {
        setFieldMapping(hdeCASEMapping);
    }, []);

    useEffect(() => {
        if (
            savedMapping &&
            savedMapping.mapping &&
            Object.keys(savedMapping.mapping).length === 0 &&
            headers &&
            headers.length &&
            fieldMapping
        ) {
            let mappedHeaders = {};
            // eslint-disable-next-line array-callback-return
            fieldMapping.map((field, i) => {
                mappedHeaders = {
                    ...mappedHeaders,
                    [field.id]: null,
                };
            });
            // eslint-disable-next-line array-callback-return
            headers.map((header, i) => {
                const values = utils.findAllByKey(fieldMapping, 'id');
                if (values.includes(header)) {
                    const hdeIndex = values.indexOf(header as string);
                    mappedHeaders = {
                        ...mappedHeaders,
                        [values[hdeIndex]]: i,
                    };
                }
            });

            // create array of selected fields by filtering out any values that are null
            const selectedFields = Object.keys(
                Object.entries(mappedHeaders).reduce(
                    (a, [k, v]) => (v === null ? a : ((a[k] = v), a)),
                    {}
                )
            );

            setUsedFields(selectedFields);
            if (Object.values(mappedFields).length === 0) {
                setMappedFields(mappedHeaders);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers, fieldMapping, savedMapping]);

    const handleChange = (event: SelectChangeEvent, item, index) => {
        let newMapped;
        // remove old reference of value in case it is changed
        const updatedMapIndex = Object.fromEntries(
            Object.entries(mappedFields).filter(
                ([key, value]) => value !== index
            )
        );

        if (event.target.value === null) {
            const replacedKey = Object.keys(mappedFields).find(
                (key) => mappedFields[key] === index
            );
            newMapped = {
                ...updatedMapIndex,
                [replacedKey]: null,
            };
        } else {
            const updatedMapIndex = Object.fromEntries(
                Object.entries(mappedFields).filter(
                    ([key, value]) => value !== index
                )
            );
            newMapped = {
                ...updatedMapIndex,
                [event.target.value]: index,
            };
        }

        // create array of selected fields by filtering out any values that are null
        const selectedFields = Object.keys(
            Object.entries(newMapped).reduce(
                (a, [k, v]) => (v === null ? a : ((a[k] = v), a)),
                {}
            )
        );

        setMappedFields(newMapped);
        setUsedFields(selectedFields);
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography
                    variant={'body1'}
                    sx={{ textAlign: 'center' }}
                    color={'secondary'}
                >
                    Complete the following mapping of the column headers from
                    your XSLX with our HDE fields. Match as many fields as
                    possible to maximize your data import.{' '}
                    <b>
                        For minimal mapping, the case_id is the only required
                        mapped field
                    </b>
                    .
                </Typography>
                <Divider sx={{ m: 2 }} />
                {!headers || headers.length === 0 ? (
                    <Grid container justifyContent={'center'}>
                        <Grid item>
                            <CircularProgress />
                        </Grid>
                    </Grid>
                ) : (
                    <List>
                        {Object.keys(mappedFields).length > 0 &&
                            headers.map((header, i) => {
                                return (
                                    <div key={`mapping-field-${i}`}>
                                        <ListItem>
                                            <Grid
                                                container
                                                alignItems={'center'}
                                                justifyContent={'center'}
                                                spacing={1}
                                            >
                                                <Grid item xs={5}>
                                                    <ListItemText
                                                        primary={header}
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <ListItemIcon>
                                                        <CompareArrowsIcon />
                                                    </ListItemIcon>
                                                </Grid>
                                                <Grid item xs={5}>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="demo-simple-select-label">
                                                            HDE Field
                                                        </InputLabel>
                                                        <Select
                                                            labelId="demo-simple-select-label"
                                                            id="demo-simple-select"
                                                            value={Object.keys(
                                                                mappedFields
                                                            ).find(
                                                                (key) =>
                                                                    mappedFields[
                                                                        key
                                                                    ] === i
                                                            )}
                                                            label="HDE Field"
                                                            onChange={(e) =>
                                                                handleChange(
                                                                    e,
                                                                    header,
                                                                    i
                                                                )
                                                            }
                                                        >
                                                            <MenuItem
                                                                value={null}
                                                            >
                                                                <em>
                                                                    Remove
                                                                    Mapping
                                                                </em>
                                                            </MenuItem>
                                                            {fieldMapping &&
                                                                fieldMapping.map(
                                                                    (
                                                                        field,
                                                                        i
                                                                    ) => {
                                                                        return (
                                                                            <MenuItem
                                                                                value={
                                                                                    field.id
                                                                                }
                                                                                disabled={usedFields.includes(
                                                                                    field.id
                                                                                )}
                                                                            >
                                                                                {field.id ===
                                                                                'case_id'
                                                                                    ? `${field.label}*`
                                                                                    : field.label}
                                                                            </MenuItem>
                                                                        );
                                                                    }
                                                                )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item>
                                                    {Object.values(
                                                        mappedFields
                                                    ).includes(i) ? (
                                                        <CheckCircleIcon
                                                            color={'success'}
                                                        />
                                                    ) : (
                                                        <ErrorOutlineIcon
                                                            color={'warning'}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </ListItem>
                                        <Divider />
                                    </div>
                                );
                            })}
                    </List>
                )}
            </Grid>
        </Grid>
    );
};

export default DataMapping;
